import React from 'react';
import { Link } from 'react-router-dom';

export const STOCK_HISTORY_TYPES = {
  order_line_edit: 'Зміна позиції замовлення',
  order_merge: 'Об’єднання замовлень',
  order_line_transfer: 'Перенесення товару між замовленнями',
  product_state_edit: 'Зміна стану товару',
  product_create: 'Створення товару',
  admin_edit: 'Редагування з адмінки',
  admin_receipt: 'Поштучне внесення на склад',
  incoming_receipt_bulk: 'Масове внесення з поставки',
  invoice_import: 'Імпорт накладної',
  sale: 'Продаж',
  preorder: 'Передзамовлення (списання з доступної кількості)',
  preorder_fulfill: 'Розподіл надходження у замовлення',
  preorder_pool_to_stock: 'Перенесення з передзамовлення в наявність',
  preorder_complete: 'Завершення передзамовлення',
  order_cancel_return: 'Повернення на склад (скасування замовлення)',
  order_status_return: 'Повернення на склад (повернення замовлення)',
  order_return_qty_decrease: 'Повернення на склад (зменшення кількості в замовленні)',
  order_return_line_removed: 'Повернення на склад (видалення товару з замовлення)',
  order_return_order_deleted:
    'Повернення на склад (видалення останнього товару, порожнє замовлення)',
  order_uncancel_deduct: 'Списання зі складу (повторне резервування після скасування)',
  order_line_deduct: 'Списання зі складу (збільшення кількості в замовленні)',
  /** Старі записи до розбиття на order_return_* */
  order_line_return: 'Повернення на склад (редагування замовлення, старий запис)',
  // застарілі коди (до зміни типів)
  import: 'Імпорт накладної (старий запис)',
  manual: 'Ручна операція (старий запис)',
  receipt: 'Надходження (старий запис)',
};

function parseCreatedAtParts(val) {
  if (val == null) return null;
  if (Array.isArray(val) && val.length >= 5) {
    const [y, mo, d, h = 0, mi = 0] = val;
    return { y, mo, d, h, mi };
  }
  if (typeof val === 'string') {
    const parsed = new Date(val);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
      y: parsed.getFullYear(),
      mo: parsed.getMonth() + 1,
      d: parsed.getDate(),
      h: parsed.getHours(),
      mi: parsed.getMinutes(),
    };
  }
  return null;
}

/** DD.MM HH:mm у поточному році; DD.MM.YYYY HH:mm для інших років */
export function formatStockHistoryDate(val) {
  const parts = parseCreatedAtParts(val);
  if (!parts) return val == null ? '—' : String(val);
  const { y, mo, d, h, mi } = parts;
  const pad = n => String(n).padStart(2, '0');
  const currentYear = new Date().getFullYear();
  if (y === currentYear) {
    return `${pad(d)}.${pad(mo)} ${pad(h)}:${pad(mi)}`;
  }
  return `${pad(d)}.${pad(mo)}.${y} ${pad(h)}:${pad(mi)}`;
}

export function fmtPrice(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}

const HASH_LINK_LOOKBACK = 56;

/** «замовлення» окремим словом, не частина «передзамовлення» (перед «з» має бути не літера). */
function lastStandaloneOrderMentionIndex(beforeLower) {
  const re = /(?:^|[^а-яіїєґ0-9])замовлення(?:[^а-яіїєґ0-9]|$)/gi;
  let last = -1;
  let m;
  while ((m = re.exec(beforeLower)) !== null) {
    last = m.index;
  }
  return last;
}

/**
 * Куди вести `#123`: надходження (поставка) чи замовлення.
 * Евристика: ключові слова перед `#`; якщо обидва типи — ближче до `#` перемагає; інакше за типом рядка історії.
 */
function resolveHashLinkTarget(beforeSliceLower, stockHistoryType) {
  const shipPos = Math.max(
    beforeSliceLower.lastIndexOf('надходження'),
    beforeSliceLower.lastIndexOf('поставк'),
    beforeSliceLower.lastIndexOf('incoming'),
  );
  const orderPos = lastStandaloneOrderMentionIndex(beforeSliceLower);
  if (orderPos >= 0 && shipPos >= 0) {
    return orderPos > shipPos ? 'order' : 'shipment';
  }
  if (orderPos >= 0) return 'order';
  if (shipPos >= 0) return 'shipment';
  if (stockHistoryType === 'incoming_receipt_bulk') return 'shipment';
  return 'order';
}

/** `#digits` у коментарі → посилання на замовлення або надходження залежно від контексту. */
export function renderCommentWithSmartLinks(text, stockHistoryType) {
  if (text == null || String(text).trim() === '') return '—';
  const s = String(text);
  const re = /#(\d+)/g;
  const parts = [];
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > lastIndex) {
      parts.push(<span key={`c-${key++}`}>{s.slice(lastIndex, m.index)}</span>);
    }
    const id = m[1];
    const start = Math.max(0, m.index - HASH_LINK_LOOKBACK);
    const beforeLower = s.slice(start, m.index).toLowerCase();
    const target = resolveHashLinkTarget(beforeLower, stockHistoryType);
    const to = target === 'shipment' ? `/admin/incoming-shipments/${id}` : `/admin/order/${id}`;
    parts.push(
      <Link
        key={`c-${key++}`}
        to={to}
        className='text-primary'
        title={target === 'shipment' ? 'Відкрити надходження (поставку)' : 'Відкрити замовлення'}
      >
        {m[0]}
      </Link>,
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < s.length) {
    parts.push(<span key={`c-${key++}`}>{s.slice(lastIndex)}</span>);
  }
  return parts.length > 0 ? <>{parts}</> : s;
}


export function StockMovementDetails({ row }) {
  const signed = value => `${value > 0 ? '+' : ''}${value}`;
  return (
    <div className='small'>
      {row.orderId != null && <div><Link to={`/admin/order/${row.orderId}`}>Замовлення #{row.orderId}</Link></div>}
      {row.shipmentId != null && <div><Link to={`/admin/incoming-shipments/${row.shipmentId}`}>Поставка #{row.shipmentId}</Link></div>}
      {row.preorderQtyBefore != null && <div>Ліміт передзамовлення: {row.preorderQtyBefore} → {row.preorderQtyAfter} ({signed(row.preorderQtyChange || 0)})</div>}
      {Boolean(row.stockReservedChange) && <div>Резерв зі складу: {signed(row.stockReservedChange)}</div>}
      {Boolean(row.preorderReservedChange) && <div>Резерв передзамовлення: {signed(row.preorderReservedChange)}</div>}
    </div>
  );
}
